import { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Switch,
  FormControlLabel,
  CircularProgress,
  Divider,
} from "@mui/material";
import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import AsyncSelect from "react-select/async";
import Select from "react-select";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import Swal from "sweetalert2";

import UsersApi from "../../../Classes/API/UsersApi";
import SessionsApi from "../../../Classes/API/Administration/SessionsApi";
import searchSessionsReq from "../../../Classes/Models/Requests/Administration/CreateSessionReq";
import CreateUserReq from "../../../Classes/Models/Requests/Users/CreateUserReq";
import CreateBatchAccountingSessionsReq from "../../../Classes/Models/Requests/Accounting/CreateBatchAccountingSessionsReq";
import AccountingSessionBatchItem from "../../../Classes/Models/Requests/Accounting/AccountingSessionBatchItem";
import CountrySelector from "layouts/authentication/sign-up/CountrySelector";
import { NationalityGroupOptions, GenderOptions } from "utils/userConstants";

const NewClientEvalModal = ({ open, onClose, onSuccess, tenantId }) => {
  const [showExtraFields, setShowExtraFields] = useState(false);
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    number: "",
    address: "",
    nationality: "",
    nationalityGroup: null,
    gender: "",
    birthdayDate: "",
    referral: "",
    nationalPassportId: "",
    relativeNumber: "",
    chronicDiseases: "",
    sensitivity: "",
    medications: "",
    selectedSession: null,
  });

  useEffect(() => {
    if (open) loadDefaultSession();
  }, [open]);

  const loadDefaultSession = async () => {
    const sessions = await fetchSessions("Evaluation");
    const found =
      sessions.find((s) => s.label.toLowerCase().includes("evaluation")) ||
      sessions[0];
    setFormData((prev) => ({ ...prev, selectedSession: found }));
  };

  const fetchSessions = async (inputValue) => {
    const req = new searchSessionsReq();
    req.Name = inputValue;
    const res = await SessionsApi.SearchSessions(req);
    return res.status === "Success"
      ? res.data.list.map((s) => ({ value: s, label: s.name }))
      : [];
  };

  const handleSave = async () => {
    if (
      !formData.name ||
      !formData.number ||
      !formData.selectedSession ||
      !formData.nationalityGroup
    ) {
      Swal.fire(
        "Error",
        "Name, Phone, Session, and Nationality Group are mandatory",
        "error",
      );
      return;
    }

    setLoading(true);
    try {
      const userReq = new CreateUserReq();
      userReq.Name = formData.name;
      userReq.Number = "+" + formData.number;
      userReq.Address = formData.address;
      userReq.Nationality = formData.nationality;
      userReq.NationalityGroup = formData.nationalityGroup.value;
      userReq.Gender = formData.gender;
      userReq.BirthdayDate = formData.birthdayDate;
      userReq.Referral = formData.referral;
      userReq.NationalId = formData.nationalPassportId;
      userReq.RelativeNumber = formData.relativeNumber;
      userReq.ChronicDiseases = formData.chronicDiseases;
      userReq.Sensetivity = formData.sensitivity;
      userReq.Medications = formData.medications;
      userReq.UserType = "Patient";
      userReq.isActivated = true;
      userReq.TenantId = tenantId;

      const userRes = await UsersApi.CreateUser(userReq);
      if (userRes.status !== "Success")
        throw new Error("Failed to create client profile");

      const batchReq = new CreateBatchAccountingSessionsReq();
      batchReq.PatientId = userRes.data.getPatient.id;

      const sessionObj = formData.selectedSession.value;
      const item = new AccountingSessionBatchItem();
      item.SessionId = sessionObj.id;

      const group = formData.nationalityGroup.value;
      let price = 0;
      if (group === "Egyptian") price = sessionObj.egyptianPrice;
      else if (group === "Arab") price = sessionObj.arabPrice;
      else price = sessionObj.otherPrice;

      item.OriginalPrice = price || 0;
      item.FinalPrice = price || 0;
      item.Discount = 0;

      batchReq.Sessions = [item];
      const sessionRes =
        await SessionsApi.CreateBatchAccountingSessions(batchReq);

      if (sessionRes.status === "Success") {
        Swal.fire(
          "Success",
          "Client and Evaluation session created",
          "success",
        );
        onSuccess();
        onClose();
        setFormData({
          name: "",
          number: "",
          address: "",
          nationality: "",
          nationalityGroup: null,
          gender: "",
          birthdayDate: "",
          referral: "",
          nationalPassportId: "",
          relativeNumber: "",
          chronicDiseases: "",
          sensitivity: "",
          medications: "",
          selectedSession: null,
        });
      }
    } catch (err) {
      Swal.fire("Error", err.message || "Something went wrong", "error");
    } finally {
      setLoading(false);
    }
  };

  const selectStyles = {
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    control: (base) => ({
      ...base,
      height: "45px",
      borderRadius: "8px",
      borderColor: "#d2d6da",
    }),
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ fontWeight: "bold" }}>
        Quick Client Evaluation
      </DialogTitle>
      <DialogContent dividers>
        <MDBox display="flex" flexDirection="column" gap={2}>
          <Typography variant="button" fontWeight="bold" color="info">
            Main Information
          </Typography>
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
            <MDInput
              label="Full Name *"
              fullWidth
              value={formData.name}
              onChange={(e) =>
                setFormData({ ...formData, name: e.target.value })
              }
            />
            <PhoneInput
              country="eg"
              value={formData.number}
              onChange={(val) => setFormData({ ...formData, number: val })}
              containerStyle={{ width: "100%" }}
              inputStyle={{
                width: "100%",
                height: "45px",
                borderRadius: "8px",
              }}
            />
          </Box>

          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
            <Box>
              <Typography
                variant="caption"
                fontWeight="bold"
                sx={{ mb: 1, display: "block" }}
              >
                Select Session *
              </Typography>
              <AsyncSelect
                cacheOptions
                defaultOptions
                loadOptions={fetchSessions}
                value={formData.selectedSession}
                menuPortalTarget={document.body}
                styles={selectStyles}
                onChange={(val) =>
                  setFormData({ ...formData, selectedSession: val })
                }
              />
            </Box>
            <Box>
              <Typography
                variant="caption"
                fontWeight="bold"
                sx={{ mb: 1, display: "block" }}
              >
                Nationality Group *
              </Typography>
              <Select
                options={NationalityGroupOptions}
                value={formData.nationalityGroup}
                menuPortalTarget={document.body}
                styles={selectStyles}
                onChange={(val) =>
                  setFormData({ ...formData, nationalityGroup: val })
                }
              />
            </Box>
          </Box>

          <Divider sx={{ my: 1 }} />

          <FormControlLabel
            control={
              <Switch
                checked={showExtraFields}
                onChange={(e) => setShowExtraFields(e.target.checked)}
              />
            }
            label="Add More Details"
          />

          {showExtraFields && (
            <MDBox
              display="flex"
              flexDirection="column"
              gap={2}
              sx={{ mt: 1, pb: 2 }}
            >
              <Box
                sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}
              >
                <MDInput
                  label="Address"
                  fullWidth
                  value={formData.address}
                  onChange={(e) =>
                    setFormData({ ...formData, address: e.target.value })
                  }
                />
                <MDInput
                  label="Birthday"
                  type="date"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={formData.birthdayDate}
                  onChange={(e) =>
                    setFormData({ ...formData, birthdayDate: e.target.value })
                  }
                />
                <Box>
                  <Select
                    options={GenderOptions}
                    menuPortalTarget={document.body}
                    styles={selectStyles}
                    onChange={(e) =>
                      setFormData({ ...formData, gender: e.value })
                    }
                  />
                </Box>
                <CountrySelector
                  label="Nationality"
                  SetNationality={(val) =>
                    setFormData({ ...formData, nationality: val })
                  }
                />
                <MDInput
                  label="National ID"
                  fullWidth
                  value={formData.nationalPassportId}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      nationalPassportId: e.target.value,
                    })
                  }
                />
                <MDInput
                  label="Relative Phone"
                  fullWidth
                  value={formData.relativeNumber}
                  onChange={(e) =>
                    setFormData({ ...formData, relativeNumber: e.target.value })
                  }
                />
                <MDInput
                  label="Referral"
                  fullWidth
                  value={formData.referral}
                  onChange={(e) =>
                    setFormData({ ...formData, referral: e.target.value })
                  }
                />
              </Box>
              <Typography
                variant="button"
                fontWeight="bold"
                color="error"
                sx={{ mt: 1 }}
              >
                Medical Info
              </Typography>
              <MDInput
                label="Chronic Diseases"
                multiline
                rows={2}
                fullWidth
                value={formData.chronicDiseases}
                onChange={(e) =>
                  setFormData({ ...formData, chronicDiseases: e.target.value })
                }
              />
              <MDInput
                label="Sensitivities"
                multiline
                rows={2}
                fullWidth
                value={formData.sensitivity}
                onChange={(e) =>
                  setFormData({ ...formData, sensitivity: e.target.value })
                }
              />
              <MDInput
                label="Medications"
                multiline
                rows={2}
                fullWidth
                value={formData.medications}
                onChange={(e) =>
                  setFormData({ ...formData, medications: e.target.value })
                }
              />
            </MDBox>
          )}
        </MDBox>
      </DialogContent>
      <DialogActions sx={{ p: 3 }}>
        <Button onClick={onClose} color="secondary">
          Cancel
        </Button>
        <MDButton
          variant="gradient"
          color="info"
          onClick={handleSave}
          disabled={loading}
          sx={{ minWidth: "150px" }}
        >
          {loading ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            "Save & Create Session"
          )}
        </MDButton>
      </DialogActions>
    </Dialog>
  );
};

export default NewClientEvalModal;
